import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Sparkles, Plus, Edit2, Trash2, Users, Clock, MapPin, Star, HeartHandshake } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * PrivatePayView — schedule view for the Private Pay service line.
 *
 * A "session" = unique (date, program_id). Each session has:
 *   - 1-3 staff shifts (one row per staff); one may be marked as facilitator.
 *   - Optional list of assigned volunteers (from the /volunteers pipeline).
 */
export default function PrivatePayView({ shifts, date, programs, employees, onAddSession, onEditSession, onAssignStaff, onUnassignStaff, onDeleteSession }) {
  const { api } = useAuth();
  const [sessionVolunteers, setSessionVolunteers] = useState({}); // {"date::progId": [vol,...]}
  const [availableVolunteers, setAvailableVolunteers] = useState([]);
  const [volDialogOpen, setVolDialogOpen] = useState(null);       // {program_id, currentIds}

  const sessions = useMemo(() => {
    const map = new Map();
    for (const s of shifts) {
      if (s.service_line !== 'private_pay') continue;
      const key = s.program_id || `_no_program_${s.id}`;
      if (!map.has(key)) {
        map.set(key, {
          program_id: s.program_id,
          time_in: s.time_in,
          time_out: s.time_out,
          location: s.location || '',
          staff_shifts: [],
        });
      }
      map.get(key).staff_shifts.push(s);
    }
    return Array.from(map.values());
  }, [shifts]);

  const programById = useMemo(() => Object.fromEntries((programs || []).map(p => [p.id, p])), [programs]);
  const empById = useMemo(() => Object.fromEntries((employees || []).map(e => [e.id, e])), [employees]);

  // Fetch approved + active volunteers
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: approved }, { data: active }] = await Promise.all([
          api.get('/volunteers?status=approved'),
          api.get('/volunteers?status=active'),
        ]);
        if (cancelled) return;
        setAvailableVolunteers([...(approved || []), ...(active || [])]);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Load volunteer assignments per session
  const sessionKey = sessions.map(s => s.program_id).filter(Boolean).join('|');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map = {};
      for (const s of sessions) {
        if (!s.program_id) continue;
        try {
          const { data } = await api.get(`/private-pay/session-volunteers?date=${date}&program_id=${s.program_id}`);
          if (cancelled) return;
          map[`${date}::${s.program_id}`] = data || [];
        } catch { /* silent */ }
      }
      if (!cancelled) setSessionVolunteers(map);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, sessionKey]);

  const saveVolunteers = async (progId, ids) => {
    try {
      await api.post('/private-pay/session-volunteers', { date, program_id: progId, volunteer_ids: ids });
      const { data } = await api.get(`/private-pay/session-volunteers?date=${date}&program_id=${progId}`);
      setSessionVolunteers(m => ({ ...m, [`${date}::${progId}`]: data || [] }));
      toast.success('Volunteers updated');
      setVolDialogOpen(null);
    } catch {
      toast.error('Could not save volunteers');
    }
  };

  const toggleFacilitator = async (shift) => {
    try {
      await api.put(`/shifts/${shift.id}`, { is_facilitator: !shift.is_facilitator });
      toast.success(shift.is_facilitator ? 'Facilitator cleared' : 'Marked as facilitator');
      window.dispatchEvent(new CustomEvent('rt:shifts.changed'));
    } catch {
      toast.error('Could not update facilitator');
    }
  };

  const todayDow = new Date(date + 'T12:00:00').getDay();
  const recurringToday = (programs || []).filter(p => p.is_active && (p.recurring_days || []).includes(todayDow));
  const scheduledProgramIds = new Set(sessions.map(s => s.program_id));
  const suggestedTodaySessions = recurringToday.filter(p => !scheduledProgramIds.has(p.id));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Private Pay Sessions</h3>
          <p className="text-[11px] text-[#8E85A0]">{sessions.length} session{sessions.length === 1 ? '' : 's'} on {date}</p>
        </div>
        <Button onClick={() => onAddSession(null)} className="bg-[#0077B6] hover:bg-[#005A8C] text-white gap-1 h-9" data-testid="pp-add-session-btn">
          <Plus className="h-4 w-4" /> Add Session
        </Button>
      </div>

      {suggestedTodaySessions.length > 0 && (
        <Card className="border-dashed border-[#4FC3F7] bg-[#E1F5FE]/40">
          <CardContent className="p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#01579B] mb-2">Recurring today — not scheduled yet</p>
            <div className="flex flex-wrap gap-2">
              {suggestedTodaySessions.map(p => (
                <Button key={p.id} variant="outline" size="sm"
                  onClick={() => onAddSession(p.id)}
                  className="border-[#4FC3F7] text-[#01579B] hover:bg-white gap-1 h-8 text-xs"
                  data-testid={`pp-suggest-${p.id}`}>
                  <Plus className="h-3 w-3" /> {p.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {sessions.length === 0 ? (
        <div className="text-center py-10 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg" data-testid="pp-empty-state">
          <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50 text-[#4FC3F7]" />
          <p className="text-sm">No private pay sessions scheduled for this day.</p>
          <p className="text-[11px] mt-1">Click "Add Session" to schedule one.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="pp-sessions-list">
          {sessions.map(sess => {
            const prog = programById[sess.program_id];
            const color = prog?.color || '#0077B6';
            const vols = sessionVolunteers[`${date}::${sess.program_id}`] || [];
            return (
              <Card key={sess.program_id || sess.staff_shifts[0]?.id} className="border-l-4 shadow-sm bg-white" style={{ borderLeftColor: color }} data-testid={`pp-session-${sess.program_id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-bold text-[#2D1B4E]">{prog?.name || 'Untitled Program'}</h4>
                        <Badge className="text-[10px] bg-[#E1F5FE] text-[#01579B] border-[#4FC3F7]">Private Pay</Badge>
                        {prog?.price_per_session > 0 && (
                          <span className="text-[11px] font-bold text-[#0077B6]">${prog.price_per_session.toFixed(2)}/session</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-[#8E85A0] flex-wrap">
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{sess.time_in}–{sess.time_out}</span>
                        {sess.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{sess.location}</span>}
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{sess.staff_shifts.length}/3 staff · {vols.length} vol</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => onEditSession(sess)} className="h-7 w-7 text-[#8E85A0] hover:text-[#0077B6]" data-testid={`pp-edit-${sess.program_id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => onDeleteSession(sess)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`pp-del-${sess.program_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>

                  {/* Staff row */}
                  <div className="mt-2 pt-2 border-t border-[#F0EDF6]">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8E85A0] mb-1.5">Staff</p>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {sess.staff_shifts.map(sh => {
                        const emp = empById[sh.employee_id];
                        const isFac = !!sh.is_facilitator;
                        return (
                          <div key={sh.id}
                               className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs ${isFac ? 'bg-[#FFF3E0] border border-[#FFB74D] text-[#E65100]' : 'bg-[#F0EDF6] text-[#2D1B4E]'}`}
                               data-testid={`pp-staff-${sh.id}`}>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold ${isFac ? 'bg-[#FB8C00]' : 'bg-[#9B7ED8]'}`}>
                              {emp?.name?.charAt(0) || '?'}
                            </div>
                            <span>{emp?.name || 'Unassigned'}</span>
                            {isFac && <Star className="h-3 w-3 fill-current" />}
                            <button
                              onClick={() => toggleFacilitator(sh)}
                              title={isFac ? 'Remove facilitator role' : 'Mark as facilitator'}
                              className={`ml-1 text-[10px] ${isFac ? 'text-[#E65100]' : 'text-gray-500 hover:text-orange-600'}`}
                              data-testid={`pp-facilitator-${sh.id}`}>
                              {isFac ? 'Facilitator ✓' : 'Make facilitator'}
                            </button>
                            <button onClick={() => onUnassignStaff(sh)} className="text-[#8E85A0] hover:text-[#D9534F] ml-1" data-testid={`pp-unassign-${sh.id}`}>×</button>
                          </div>
                        );
                      })}
                      {sess.staff_shifts.length < 3 && (
                        <Button size="sm" variant="outline" onClick={() => onAssignStaff(sess)}
                          className="h-7 px-2 border-dashed border-[#4FC3F7] text-[#0077B6] hover:bg-[#E1F5FE] text-xs gap-1"
                          data-testid={`pp-assign-${sess.program_id}`}>
                          <Plus className="h-3 w-3" /> Assign staff
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Volunteers row */}
                  <div className="mt-2 pt-2 border-t border-[#F0EDF6]">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8E85A0] flex items-center gap-1">
                        <HeartHandshake className="h-3 w-3" /> Volunteers
                      </p>
                      <button
                        onClick={() => setVolDialogOpen({
                          program_id: sess.program_id,
                          currentIds: vols.map(v => v.id),
                        })}
                        className="text-[10px] text-[#0077B6] hover:underline"
                        data-testid={`pp-vol-manage-${sess.program_id}`}>
                        Manage
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {vols.length === 0 ? (
                        <span className="text-[11px] italic text-[#8E85A0]">No volunteers assigned</span>
                      ) : (
                        vols.map(v => (
                          <div key={v.id} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#FCE4EC] text-xs text-[#B71C5C]" data-testid={`pp-vol-${v.id}`}>
                            <HeartHandshake className="h-3 w-3" />
                            <span>{v.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!volDialogOpen} onOpenChange={o => !o && setVolDialogOpen(null)}>
        <DialogContent data-testid="pp-vol-dialog">
          <DialogHeader><DialogTitle>Assign volunteers to session</DialogTitle></DialogHeader>
          {volDialogOpen && (
            <VolunteerPicker
              all={availableVolunteers}
              initialIds={volDialogOpen.currentIds}
              onSave={(ids) => saveVolunteers(volDialogOpen.program_id, ids)}
              onCancel={() => setVolDialogOpen(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VolunteerPicker({ all, initialIds, onSave, onCancel }) {
  const [selected, setSelected] = useState(new Set(initialIds));
  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  return (
    <>
      <div className="max-h-64 overflow-y-auto py-2 space-y-1" data-testid="pp-vol-picker-list">
        {all.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            No approved or active volunteers yet.
          </p>
        ) : all.map(v => (
          <label key={v.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${selected.has(v.id) ? 'bg-[#FCE4EC]' : 'hover:bg-gray-50'}`}
                 data-testid={`pp-vol-option-${v.id}`}>
            <input type="checkbox" checked={selected.has(v.id)} onChange={() => toggle(v.id)} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{v.name}</div>
              <div className="text-xs text-gray-500">{v.email} · {v.program || 'general'}</div>
            </div>
          </label>
        ))}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSave(Array.from(selected))} data-testid="pp-vol-save-btn">Save assignments</Button>
      </DialogFooter>
    </>
  );
}
